import { Phone, Stop, CircleNotch, Microphone, MicrophoneSlash, ArrowsInSimple } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import type { VoiceCallState } from './use-voice-call';
import type { VoiceStatus } from './voice-event-router';

interface VoiceCallBarProps {
  state: VoiceCallState;
  status: VoiceStatus;
  /** Last user transcript heard so far in this call. */
  userTranscript: string;
  /** Streaming assistant transcript for the in-flight reply (cleared on turn done). */
  assistantStreaming: string;
  muted: boolean;
  onToggleMute: () => void;
  onStop: () => void;
  /**
   * FRONT-254: present only on a hands-free call. Closes the chat window and
   * keeps the call going in the strip at the top of the app.
   */
  onMinimize?: () => void;
  /** FRONT-254: seconds before a quiet hands-free call ends, while it warns. */
  quietSecondsLeft?: number | null;
  /** FRONT-254: keep a quiet hands-free call going. */
  onKeepCall?: () => void;
}

const STATUS_LABEL: Record<VoiceStatus, string> = {
  listening: 'Listening',
  thinking: 'Thinking…',
  speaking: 'Speaking',
};

/** The status line above the transcript. Muted outranks the live status —
 * a listening/speaking label next to a microphone that isn't hearing
 * anything would be actively misleading. */
export function callBarLabel(state: VoiceCallState, status: VoiceStatus, muted: boolean): string {
  if (state === 'connecting') return 'Connecting…';
  if (muted) return 'Muted';
  return STATUS_LABEL[status];
}

/**
 * FRONT-2063: disabled + its explanation are one decision, per the repo's
 * disabled-control rule — a state is disabled iff it carries a reason. A
 * press before the microphone is granted would silently do nothing, so the
 * control waits for the live call; the phone call screen shares this gate,
 * so every non-'live' state (idle, connecting, ending, error) shares the one
 * "not yet connected" reason on both transports.
 */
export function muteControl(
  state: VoiceCallState,
  muted: boolean,
): { disabled: boolean; tooltip: string; label: 'Mute' | 'Unmute' } {
  const label = muted ? 'Unmute' : 'Mute';
  if (state !== 'live') {
    return { disabled: true, tooltip: 'Mute is available once the call connects', label };
  }
  return {
    disabled: false,
    tooltip: muted
      ? 'Unmute your microphone'
      : 'Mute your microphone — EDDY stops hearing you, the call keeps running',
    label,
  };
}

export function VoiceCallBar({
  state,
  status,
  userTranscript,
  assistantStreaming,
  muted,
  onToggleMute,
  onStop,
  onMinimize,
  quietSecondsLeft,
  onKeepCall,
}: VoiceCallBarProps) {
  const connecting = state === 'connecting';
  const live = state === 'live';
  const label = callBarLabel(state, status, muted);
  const mute = muteControl(state, muted);

  return (
    <div className="flex flex-col gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5 min-h-[76px]">
      <div className="flex items-center gap-3">
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          {live && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-60" />
          )}
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive" />
        </span>
        {connecting ? (
          <CircleNotch size={16} className="text-destructive shrink-0 animate-spin" />
        ) : (
          <Phone size={16} className="text-destructive shrink-0" weight="fill" />
        )}
        <div className="flex-1 flex items-baseline gap-2 min-w-0">
          <span className="text-sm font-medium text-foreground">Voice call</span>
          <span className="text-xs text-muted-foreground" data-testid="voice-call-status">
            {label}
          </span>
        </div>
        <Button
          type="button"
          onClick={onToggleMute}
          variant={muted ? 'secondary' : 'outline'}
          size="sm"
          className="gap-1.5"
          disabled={mute.disabled}
          tooltip={mute.tooltip}
          data-testid="voice-call-mute"
        >
          {muted ? <MicrophoneSlash size={14} /> : <Microphone size={14} />} {mute.label}
        </Button>
        {onMinimize && (
          <Button
            type="button"
            onClick={onMinimize}
            variant="outline"
            size="sm"
            className="gap-1.5 active:scale-[0.96]"
            tooltip="Keep talking while you use the rest of EDDY — the call moves to a strip at the top"
            data-testid="voice-call-minimize"
          >
            <ArrowsInSimple size={14} /> Minimize
          </Button>
        )}
        <Button
          type="button"
          onClick={onStop}
          variant="destructive"
          size="sm"
          className="gap-1.5"
          tooltip="End voice call"
        >
          <Stop size={14} weight="fill" /> End
        </Button>
      </div>
      {quietSecondsLeft != null && onKeepCall && (
        <div className="flex flex-wrap items-center gap-2 text-sm" role="status" data-testid="voice-call-quiet">
          <span className="min-w-0 flex-1">
            Nobody has spoken for 5 minutes. Ending the call in{' '}
            <span className="font-semibold tabular-nums">{quietSecondsLeft} s</span>.
          </span>
          <Button type="button" size="sm" className="active:scale-[0.96]" onClick={onKeepCall} data-testid="voice-call-keep">
            Keep call
          </Button>
        </div>
      )}
      {(userTranscript || assistantStreaming) && (
        <div className="text-xs text-muted-foreground space-y-0.5">
          {userTranscript && (
            <div>
              <span className="font-medium text-foreground/70">You: </span>
              {userTranscript}
            </div>
          )}
          {assistantStreaming && (
            <div>
              <span className="font-medium text-foreground/70">EDDY: </span>
              {assistantStreaming}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
